import { cn } from "@breenova/ui";
import { Link } from "react-router";

import type { PedigreeNode } from "@/lib/birds";

/**
 * A pedigree drawn as generations side by side: the bird, then its parents, then theirs.
 *
 * Each generation is one column, spread evenly down the tree's height, and every column
 * after the first carries a bracket rule down its left edge. Laid out by column rather
 * than recursively, because with Ahnentafel numbering a generation is simply a range —
 * positions 2^g to 2^(g+1)-1 — and spreading a range evenly is what lines a bird up
 * against the two parents beside it.
 */
export interface PedigreeTreeProps {
  /** Every recorded position, keyed by Ahnentafel number. */
  nodes: Map<number, PedigreeNode>;
  /** How many generations to draw above the bird. */
  depth: number;
}

export function PedigreeTree({ nodes, depth }: PedigreeTreeProps) {
  const columns = Array.from({ length: depth + 1 }, (_, generation) =>
    Array.from({ length: 2 ** generation }, (_, index) => 2 ** generation + index),
  );

  return (
    <div
      className="flex items-stretch gap-2.5"
      // Every slot of the last generation gets at least a node's height, so a deep tree
      // grows taller rather than squeezing its outermost birds on top of each other.
      style={{ height: `calc(var(--spacing) * ${Math.max(100, 2 ** depth * 14)})` }}
    >
      {columns.map((positions, generation) => (
        <div
          key={generation}
          className={cn(
            "flex shrink-0 flex-col",
            generation === 0
              ? "w-25 justify-center"
              : "w-27.5 justify-around rounded-l-bracket border-l-2 border-surface-container-highest pl-2.5",
          )}
        >
          {/* Recursion would bottom out on the data; a column never does, so a gap in the
              middle of a pedigree still leaves a full column of placeholders beside the
              birds that are recorded. That regular shape is what makes the tree readable. */}
          {positions.map((position) => {
            const node = nodes.get(position);

            return node?.birdId ? (
              <PedigreeCard key={position} node={node} generation={generation} />
            ) : (
              <UnknownCard key={position} position={position} child={nodes.get(position >> 1)} />
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * Which parent slot a position sits in. Even numbers are sires, odd are dams — a
 * property of the numbering, so it holds for an unrecorded ancestor too and the
 * placeholder can still say which side it belongs to.
 */
function roleFor(position: number): "Sire" | "Dam" {
  return position % 2 === 0 ? "Sire" : "Dam";
}

function sexMark(sex: PedigreeNode["sex"]): string {
  return sex === "Male" ? "♂" : sex === "Female" ? "♀" : "";
}

function PedigreeCard({ node, generation }: { node: PedigreeNode; generation: number }) {
  const isProband = generation === 0;
  const compact = generation >= 2;
  const mark = sexMark(node.sex);

  /*
   * The line under the ring number. The bird itself says so; a parent says which parent
   * it is; further out the column already says it, so only the sex is left. An unsexed
   * bird falls back to its slot rather than to the word "unknown", which the placeholder
   * cards own — "we do not know this bird's sex" and "we do not know this bird" have to
   * read differently.
   */
  const caption = isProband
    ? `This bird ${mark}`.trim()
    : !compact || !mark
      ? `${roleFor(node.position)} ${mark}`.trim()
      : mark;

  return (
    <Link
      to={`/birds/${node.birdId}`}
      className={cn(
        "flex flex-col gap-0.5 rounded-inset transition-opacity hover:opacity-90",
        compact ? "p-2.5" : "p-2.75",
        isProband
          ? "bg-primary-fixed-dim"
          : cn("bg-surface-container-lowest", compact ? "" : "shadow-card"),
        // A bird holding more than one position is what line-breeding looks like, and is
        // the reason the coefficient above is not zero. Worth seeing at a glance.
        node.isDuplicate ? "shadow-[0_0_0_2.75px_var(--color-primary)]" : "",
      )}
    >
      <span
        className={cn("truncate text-on-surface", compact ? "text-node-sm" : "text-node")}
        data-numeric
      >
        {node.ringNumber ?? "Unringed"}
      </span>

      {node.isDuplicate ? (
        <span className="truncate text-tab-label text-primary-strong">
          <span>Repeated</span> {mark}
        </span>
      ) : (
        <span
          className={cn(
            "truncate text-node-sub",
            isProband ? "text-on-primary-fixed" : "text-on-surface-variant",
          )}
        >
          {caption}
        </span>
      )}

      {node.isTruncated && !node.isDuplicate ? (
        <span className="text-node-sub text-on-surface-variant">More above</span>
      ) : null}
    </Link>
  );
}

/**
 * An ancestor nobody recorded.
 *
 * Dashed and muted, and present rather than omitted: a gap that closes up silently makes
 * an incomplete pedigree look complete, and completeness is exactly what a breeder is
 * reading the tree to judge. When the bird it would be a parent of is on record, the
 * card leads to that bird's form, which is where a parent is set.
 */
function UnknownCard({
  position,
  child,
}: {
  position: number;
  child: PedigreeNode | undefined;
}) {
  const role = roleFor(position);
  const className = cn(
    "flex flex-col gap-0.5 rounded-inset p-2.5",
    "border-2 border-dashed border-surface-container-highest",
  );

  const body = (
    <>
      <span className="text-node-sm text-on-surface-variant">Unknown</span>
      <span className="text-node-sub text-on-surface-variant">
        {child?.birdId ? `Add ${role.toLowerCase()}` : role}
      </span>
    </>
  );

  return child?.birdId ? (
    <Link
      to={`/birds/${child.birdId}/edit`}
      className={cn(className, "transition-colors hover:bg-surface-container-low")}
    >
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}
